import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import AppBar from "../../common/AppBar";
import { openPreview } from "../../redux/previewSlice";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import { SaveModal } from "../../common/Modals";
import VersionHistoryPopup from "../../common/VersionHistoryPopup/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";
import { generateAlphanumericSuffix } from "../../util/AppUtils";
import {
  createSubstep,
  updateSubstep,
  getAllSubstepsForStep,
} from "../../redux/stepSlice";
import { getAllVersionHistories } from "../../redux/versionHistorySlice";

import { getAuditTrailData } from "../../util/getAuditTrailData";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker";
import { buildPermissionContext } from "../../util/permissions";
import { cleanBaselineHtml } from "../../util/lexical";
import { setTaskReference } from "../Tasks/Uitlities";
import { pingTask } from "../../util/apiService";
import useSubstepData from "./hooks/useSubstepData";
import useSubstepForm from "./hooks/useSubstepForm";
import useSubstepPersistence from "./hooks/useSubstepPersistence";
import SubstepForm from "./components/SubstepForm";
import "./AddEditSubstep.css";

const AddEditSubstep = () => {
  const {
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref: step_id_param,
    substep_id_ref: substep_id_param,
  } = useParams();
  const [searchParams] = useSearchParams();
  const substep_id_query = searchParams.get("substep_id_ref");

  // substep_id_ref can come from URL params (edit mode) or query params (add mode after specific substep)
  const substep_id_ref = substep_id_param || substep_id_query;
  const location = useLocation();

  // Determine mode based on URL path
  const mode = location.pathname.includes("/edit-substep") ? "edit" : "add";

  const {
    loading,
    substepTitle,
    subheading,
    sectionTitle,
    sectionIdRef,
    stepTitle,
    stepIdRef,
    siblingOrder,
    currentSubstepData,
    allNodesData,
    currentStatus,
    item_code_type,
  } = useSubstepData({
    partId,
    revisionId,
    section_id_ref,
    step_id_ref: step_id_param,
    substep_id_ref,
    partnumber,
    revision,
    mode,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Undo-based dirty tracking for the Lexical short-description editor.
  // CAN_UNDO_COMMAND fires `true` when the user has genuinely typed something
  // and `false` when they undo all the way back to the original state.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);

  // Stable callback forwarded down to ShortDescriptionEditor via SubstepForm.
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  // Use custom hooks for form state management
  const {
    shortDescription,
    setShortDescription,
    isModalOpen,
    setModalOpen,
    comment,
    setComment,
    handleShortDescriptionChange,
    resetForm,
  } = useSubstepForm({
    mode,
    initialData: {},
  });

  // Use persistence hook
  const {
    localStorageRestored,
    hasLocalStorageData,
    saveForPreview,
    clearStoredData,
  } = useSubstepPersistence({
    mode,
    substepId: substep_id_ref,
    shortDescription,
    setShortDescription,
  });

  // Basic state (not in hooks)
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedText, setLastSavedText] = useState("");

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Ping API call every minute to keep lock alive
  useEffect(() => {
    if (!revisionId || !authUser) return;

    const pingInterval = setInterval(async () => {
      try {
        await pingTask({
          task_id: revisionId,
          user_id: authUser.id,
        });
      } catch (error) {
        console.error('Ping failed in AddEditSubstep:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(pingInterval);
    };
  }, [revisionId, authUser]);

  // Version History state
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Redux selectors
  const { versionHistories, loading: versionHistoryLoading } = useSelector(
    (state) => state.versionHistory,
  );

  const versionHistoryBreadcrumbFallback = useMemo(() => {
    const bits = [
      partnumber,
      revision,
      sectionTitle,
      stepTitle,
      substepTitle,
    ].filter((x) => x != null && String(x).trim() !== "");
    if (bits.length < 2) return "";
    const [pn, rev, ...rest] = bits;
    return [`${pn}`, `Rev ${rev}`, ...rest].join(" > ");
  }, [partnumber, revision, sectionTitle, stepTitle, substepTitle]);

  // Navigation protection – block if either the short description editor has
  // undo history OR another field triggered hasUnsavedChanges.
  useUnsavedChangesBlocker(hasUnsavedChanges || isShortDescriptionDirty);

  // AppBar back handler
  const handleAppBarBack = useCallback(() => {
    // Check for unsaved changes and show confirmation if needed
    if ((hasUnsavedChanges || isShortDescriptionDirty) && !isLoading) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmed) {
        return; // Don't navigate if user cancels
      }

      // User confirmed, disable blocker BEFORE navigating
      setHasUnsavedChanges(false);
      setIsShortDescriptionDirty(false);
    }

    // Navigate back - use a small timeout to ensure the blocker is disabled
    setTimeout(() => {
      window.history.back();
    }, 0);
  }, [hasUnsavedChanges, isShortDescriptionDirty, isLoading]);

  // (initialFormData removed – dirty detection is now handled via the
  //  Lexical undo history, so snapshot-based comparison is no longer needed)

  // Populate form data in edit mode
  useEffect(() => {
    if (mode === "edit" && currentSubstepData) {
      const { detail } = currentSubstepData;
      
      // Process short description to handle both plain text and HTML content
      let processedShortDescription = detail.short_description || "";
      if (processedShortDescription && !processedShortDescription.includes('<div class="editor-shell">')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        processedShortDescription = `<p>${processedShortDescription}</p>`;
      }

      // Set form fields
      const cleanedShortDescription = cleanBaselineHtml(processedShortDescription);
      setShortDescription(cleanedShortDescription);
    }
  }, [mode, currentSubstepData, setShortDescription]);

  // Fetch version history in edit mode to get the actual modifier user
  useEffect(() => {
    if (mode === "edit" && currentSubstepData) {
      const versionIdRef =
        currentSubstepData?.version_id_ref ||
        currentSubstepData?.detail?.version_id_ref;
      if (versionIdRef) {
        dispatch(getAllVersionHistories({ versionId: versionIdRef }));
      }
    }
  }, [mode, currentSubstepData, dispatch]);

  // Update last saved text when version history is loaded
  useEffect(() => {
    if (mode !== "edit" || !currentSubstepData || versionHistories.length === 0)
      return;

    // Get the latest version history entry (most recent audit)
    const latestVersion = versionHistories[0];
    if (latestVersion) {
      const userName =
        `${latestVersion.first_name || ""} ${latestVersion.last_name || ""
          }`.trim() || "Unknown User";

      const timestamp =
        latestVersion.audit_date || currentSubstepData?.updated_at;
      if (timestamp) {
        const { generateLastSavedText } = require("../../util/stepUtils");
        setLastSavedText(generateLastSavedText(timestamp, userName));
      }
    }
  }, [mode, currentSubstepData, versionHistories]);

  // Snackbar handlers
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Preview handler - Constructs draft substep for preview
  const handlePreviewClick = async () => {
    // For edit mode, save draft to localStorage for persistence
    if (mode === "edit") {
      try {
        await saveForPreview();
      } catch (err) {
        // Continue with preview even if save fails
      }
    }

    // Construct draft substep from current form data
    const substepId =
      mode === "edit" ? substep_id_ref : `draft-substep-${Date.now()}`;
    const draftSubstep = {
      sub_step_tree_node_id: substepId,
      tree_node_id: substepId, // SubstepNode expects tree_node_id
      mode: mode, // "add" or "edit"
      isDraft: true,
      sibling_order: siblingOrder,
      section_id_ref: section_id_ref, // Section this substep belongs to
      step_id_ref: step_id_param, // Step this substep belongs to
      detail: {
        short_description: shortDescription,
      },
      children:
        mode === "edit" && currentSubstepData?.children
          ? currentSubstepData.children
          : [],
    };

    dispatch(
      openPreview({
        partId,
        revisionId,
        draftSubstep,
      })
    );
  };

  // Save handler - Validation
  const handleSave = () => {
    setModalOpen(true);
  };

  // Confirm save handler - Main save logic
  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    setIsLoading(true);

    const substepData = {
      short_description: shortDescription,
      revision_id_ref: revisionId,
      section_id_ref: sectionIdRef,
      part_required_id_ref: partId,
      step_id_ref: stepIdRef,
      sibling_order: siblingOrder,
    };

    const breadcrumb_text = `${partnumber} > Rev ${revision} > ${sectionTitle} > ${stepTitle} > ${substepTitle}`;
    const pageTitle = substepTitle;

    try {
      if (mode === "add") {
        const data = await dispatch(
          createSubstep({
            data: substepData,
            comment,
            version_content: {
              action_to_show: `Save ${substepTitle} for ${partnumber}, Rev ${revision}`,
              partNumber: partnumber,
              revision,
              status: currentStatus,
              item_code_type,
              type: "substep",
            },
            allNodesData,
            partnumber,
            revision,
          })
        ).unwrap();
        if (data.success) {
          showSnackbar("Substep created successfully!", "success");
          setHasUnsavedChanges(false);
          clearStoredData();
          setIsLoading(false);
          setModalOpen(false);
          
          setTimeout(() => {
            navigate(
              `/view-substep/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_param}/${data.results.procedure_tree_ref}`
            );
          }, 1000);
        }
      } else {
        // Edit mode
        substepData.procedure_tree_id = substep_id_ref;
        substepData.sibling_order = currentSubstepData.sibling_order;

        const audit_trail_data = await getAuditTrailData({
          action: "UPDATE_SUBSTEP",
          comment: comment,
          parent_version: currentSubstepData?.version_id_ref,
          version_content: {
            action_to_show: `Edit ${substepTitle} for ${partnumber}, Rev ${revision}`,
            partNumber: partnumber,
            revision,
            status: currentStatus,
            item_code_type,
            type: "substep",
          },
        });

        substepData.audit_trail_data = audit_trail_data;

        // Update the substep
        await dispatch(
          updateSubstep({
            data: substepData,
            id: currentSubstepData?.substep_id_ref,
            allNodesData,
            partnumber,
            revision,
            tree_node_id: currentSubstepData.tree_node_id,
          })
        ).unwrap();

        // Fetch fresh data for the substep
        await dispatch(
          getAllSubstepsForStep({
            partId,
            revisionId,
            sectionId: sectionIdRef,
            stepId: stepIdRef,
          })
        );

        showSnackbar("Substep updated successfully!", "success");
        setHasUnsavedChanges(false);
        clearStoredData();
        setModalOpen(false);
        setIsLoading(false);

        setTimeout(() => {
          navigate(
            `/view-substep/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_param}/${substep_id_param}`
          );
        }, 1000);
      }
    } catch (error) {
      setIsLoading(false);
      showSnackbar(
        `Error ${mode === "add" ? "creating" : "updating"} substep: ` +
        error?.message,
        "error"
      );
    }
  };

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && currentSubstepData) {
      setTaskReference(
        dispatch,
        `Substep ${partnumber}`,
        currentSubstepData?.detail?.sub_step_tree_node_id,
        currentSubstepData?.tree_node_id,
        currentSubstepData?.ltree_path
      );
    }
  }, [mode, currentSubstepData, dispatch, partnumber]);

  const sectionDataForVH = useMemo(
    () => allNodesData?.find((s) => s.tree_node_id === section_id_ref),
    [allNodesData, section_id_ref],
  );

  const stepDataForVH = useMemo(
    () =>
      sectionDataForVH?.steps?.find(
        (st) => st.tree_node_id === step_id_param,
      ),
    [sectionDataForVH, step_id_param],
  );

  const substepVersionHistoryMatchIds = useMemo(
    () =>
      [
        substep_id_ref,
        currentSubstepData?.substep_id_ref,
        currentSubstepData?.detail?.sub_step_tree_node_id,
      ].filter(Boolean),
    [
      substep_id_ref,
      currentSubstepData?.substep_id_ref,
      currentSubstepData?.detail?.sub_step_tree_node_id,
    ],
  );

  const substepLetterSuffixForNodeHistory = useMemo(() => {
    if (!substep_id_ref || !stepDataForVH?.substeps?.length) return null;
    const idx = stepDataForVH.substeps.findIndex(
      (s) => s.tree_node_id === substep_id_ref,
    );
    if (idx < 0) return null;
    return generateAlphanumericSuffix(idx);
  }, [stepDataForVH, substep_id_ref]);

  /** Procedure-wide step index for pairing with substep letter — same as ViewSubstep. */
  const globalStepOrdinalForNodeHistory = useMemo(() => {
    if (
      !section_id_ref ||
      !step_id_param ||
      !Array.isArray(allNodesData)
    ) {
      return null;
    }
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      return intA - intB;
    };

    const sortedSections = [...allNodesData].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    const sectionIndex = sortedSections.findIndex(
      (section) => section.tree_node_id === section_id_ref,
    );
    if (sectionIndex < 0) return null;

    const sec = sortedSections[sectionIndex];
    const steps = sec?.steps || [];
    const stepIdx = steps.findIndex((st) => st.tree_node_id === step_id_param);
    if (stepIdx < 0) return null;

    const globalStepStart = sortedSections
      .slice(0, sectionIndex)
      .reduce((total, section) => total + (section.steps?.length || 0), 0);

    return globalStepStart + stepIdx + 1;
  }, [allNodesData, section_id_ref, step_id_param]);

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      currentSubstepData?.detail?.version_id_ref ||
      currentSubstepData?.version_id_ref ||
      null,
    nodesReady:
      mode === "edit" &&
      !loading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  if (loading) {
    return <Loader text="Loading substep data..." />;
  }

  return (
    <div>
      <AppBar
        onBack={handleAppBarBack}
        subTitleAboveTitle={
          <>
            {localStorage.getItem("user_name")} is{" "}
            {mode === "add" ? "Creating" : "Editing"}
          </>
        }
        title={substepTitle}
        showTablesDropdown={true}
        buttons={[
          {
            label: "Preview",
            onClick: handlePreviewClick,
          },
          {
            label: "Save",
            onClick: canEdit ? handleSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save substeps."
              : undefined,
          },
        ]}
        subheading={subheading}
        showBlackTitle={true}
        showColorTitle={false}
        subheadingAfterTitle={mode === "edit" ? lastSavedText : null}
        versionHistoryHandler={
          mode === "edit" ? () => setShowVersionHistory(true) : null
        }
      />

      <SubstepForm
        mode={mode}
        versionId={currentSubstepData?.version_id_ref || ""}
        shortDescription={shortDescription}
        onShortDescriptionChange={handleShortDescriptionChange}
        onShortDescriptionDirtyChange={handleShortDescriptionDirtyChange}
      />

      {/* Save Modal */}
      <SaveModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleConfirmSave}
        title={`Save ${substepTitle}`}
        subtitle={`For ${partnumber} > Rev ${revision} > ${sectionTitle} > ${stepTitle} > ${substepTitle}`}
        editorName={localStorage.getItem("user_name") || "Unknown User"}
        comment={comment}
        onCommentChange={(e) => setComment(e.target.value)}
        isLoading={isLoading}
        showLoader={true}
      />

      {/* Loading overlay */}
      {isLoading && (
        <Loader
          text={mode === "add" ? "Saving substep..." : "Updating substep..."}
        />
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          procedurePartNumber={partnumber}
          procedureRevision={revision}
          versionHistories={mergedVersionHistories}
          mode={mode}
          breadcrumbText={versionHistoryBreadcrumbFallback}
          revisionTreeNodes={allNodesData}
          nodeId={
            currentSubstepData?.tree_node_id || substep_id_ref || ""
          }
          nodeIdField="sub_step_tree_node_id"
          nodeHistoryMatchIds={
            Array.isArray(substepVersionHistoryMatchIds) &&
            substepVersionHistoryMatchIds.length > 0
              ? substepVersionHistoryMatchIds
              : undefined
          }
          nodeHistoryStepOrdinal={
            globalStepOrdinalForNodeHistory != null &&
            Number.isFinite(Number(globalStepOrdinalForNodeHistory))
              ? Number(globalStepOrdinalForNodeHistory)
              : undefined
          }
          nodeHistorySubstepLetterSuffix={
            typeof substepLetterSuffixForNodeHistory === "string" &&
            substepLetterSuffixForNodeHistory.trim() !== ""
              ? substepLetterSuffixForNodeHistory.trim()
              : undefined
          }
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision={
            allVersionHistories.length === 0
          }
          emptyMessage={
            mode === "add"
              ? "No version history available for new substeps"
              : "No version history available"
          }
          onViewDetails={(row) => {
            const auditId = row.audit_trail_id || row.audit_trail_id_ref;
            if (!auditId) return;
            setShowVersionHistory(false);
            navigate(`/audit-trail/preview/${auditId}`);
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
          exportCsvDisabled={versionHistoryTableLoading}
        />
      )}
    </div>
  );
};

export default AddEditSubstep;
